import { TooltipProvider } from "@foreday/ui/components/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { RoutePending, ScreenFailed } from "@/client/components/route-states";
import { forgetEarly } from "@/client/lib/early-reads";
import { loadEveryChunk } from "@/client/lib/every-chunk";
import { installShortcutHandler } from "@/client/lib/keyboard/shortcut-handler";
import { MAIN_PANEL_ID } from "@/client/lib/main-panel";
import { PENDING_MIN_MS } from "@/client/lib/pending";
import { createQueryClient, followVisits } from "@/client/lib/query-client";
import { sessionQuery } from "@/client/lib/session";
import { routeTree } from "@/client/routeTree.gen";

// Shortcuts are app-wide, not per route: one document listener, never removed.
installShortcutHandler(document);

const queryClient = createQueryClient();
// Takes the session read `index.html` started, on every route. A page that never reads it would
// leave the page-load answer to the next read, after a password reset or a sign-out.
void queryClient.prefetchQuery(sessionQuery);

// Opening an item beside a list (`?task`, `?item`) or changing a filter keeps the list's place; only a
// new page starts at the top. On a phone the panel takes the screen's place, and the window, not the
// main panel, goes to the top.
let samePage = false;

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // Query owns caching. The router's default keeps its own preloaded copy for 30 seconds, and the
  // two then disagree about how old the data is.
  defaultPreloadStaleTime: 0,
  scrollToTopSelectors: [
    () => (samePage ? null : document.getElementById(MAIN_PANEL_ID)),
  ],
  defaultPendingComponent: RoutePending,
  defaultPendingMinMs: PENDING_MIN_MS,
  defaultErrorComponent: ScreenFailed,
  Wrap: ({ children }) => (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>{children}</TooltipProvider>
    </QueryClientProvider>
  ),
});

router.subscribe("onBeforeNavigate", ({ fromLocation, pathChanged }) => {
  samePage = !pathChanged && fromLocation !== undefined;
});
followVisits(router);
const firstScreen = router.subscribe("onRendered", () => {
  firstScreen();
  forgetEarly();
});
loadEveryChunk(router, queryClient);

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return <RouterProvider router={router} />;
}
